import { Component, Input, Output, EventEmitter } from '@angular/core';
import { IKBCatalog, IKBCatalogCategory, IKBCatalogSelection } from '../interfaces/base-knowledge-catalog';
import { IKnowledgeData } from '../interfaces/knowledge-data';
import { I18nService } from 'i18n/i18n.service';
import * as _ from 'lodash';
import { ItemStateEnum } from '../interfaces/ai-bpm-knowledge-base';
import { CategoryNames } from '../enums/category-names.enum';

@Component({
  selector: 'biz-knowledge',
  templateUrl: './knowledge.component.html',
  styleUrls: ['./knowledge.component.scss']
})
export class KnowledgeComponent {
  public isCollapsed : any[]=[];
  private _knowledgeCategories: IKBCatalogCategory[];
  private _knowledgeData: IKnowledgeData;

  @Output() catalogSelectionChanged = new EventEmitter<IKBCatalogSelection>();
  @Output() removeAIBpmKnowledgeBaseEnvironmentItem = new EventEmitter<IKBCatalogSelection>();

  @Input()
  set knowledgeCategories(value: IKBCatalogCategory[]) {
    this._knowledgeCategories = value;
    if (value && value.length) {
      this.clearKnowledgeSelection();
      this.syncKnowledgeSelection();
      this.initializeCollapseState();
    }
  }
  get knowledgeCategories(): IKBCatalogCategory[] {
    return this._knowledgeCategories;
  }

  @Input()
  set knowledgeData(value: IKnowledgeData) {
    this._knowledgeData = value;
    this.clearKnowledgeSelection();
    this.syncKnowledgeSelection();
  }
  get knowledgeData(): IKnowledgeData {
    return this._knowledgeData;
  }

  // Getters de conveniencia para acceder a los datos
  get aIBpmKnowledgeBase() {
    return this._knowledgeData?.aIBpmKnowledgeBase;
  }

  get inheritanceInfo() {
    return this._knowledgeData?.inheritanceInfo;
  }

  constructor(private _i18nService: I18nService) {}

  isCategoryChecked(category: IKBCatalogCategory): boolean {
    return category.KBCatalogs.every(catalog => catalog.Selected);
  }

  onCatalogSelectionChange(idCategory: string, idCatalog: string, checked: boolean) {
    this.catalogSelectionChanged.emit({
      idCategory: idCategory,
      idCatalog: idCatalog,
      selected: checked
    } as IKBCatalogSelection);
  }

  onRemoveAIBpmKnowledgeBaseEnvironmentItem(idCategory: string, idCatalog: string) {
    this.removeAIBpmKnowledgeBaseEnvironmentItem.emit({
      idCategory: idCategory,
      idCatalog: idCatalog
    } as IKBCatalogSelection);
  }

  toggleCollapse(index: number) {
    if(this.isCollapsed[index]!=undefined)
      this.isCollapsed[index]= !this.isCollapsed[index];
    else
      this.isCollapsed[index]=true;
  }

  getToogleCollapse(index){
    if(this.isCollapsed[index])
      return this.isCollapsed[index]
    return false;
  }

  isCategoryInherited(category: IKBCatalogCategory): boolean {
    return category.Selected && 
           this.inheritanceInfo?.inheritedCategories?.has(category.Id) === true;
  }

  isCatalogInherited(catalog: IKBCatalog): boolean {
    return catalog.Selected && 
           this.inheritanceInfo?.inheritedCatalogs?.has(catalog.Id) === true;
  }

  getCategoryInheritanceTooltip(category: IKBCatalogCategory): string {
    if (this.isCategoryInherited(category)) {
      const inheritedFromText = this._i18nService.instant('AIFORPROCESSES.INHERITED_FROM');
      const parentName = this.inheritanceInfo?.inheritedCategories?.get(category.Id).parentName || '';
      return `${inheritedFromText}\n${parentName}`;
    }
    return '';
  }

  getCatalogInheritanceTooltip(catalog: IKBCatalog): string {
    if (this.isCatalogInherited(catalog)) {
      const inheritedFromText = this._i18nService.instant('AIFORPROCESSES.INHERITED_FROM');
      const parentName = this.inheritanceInfo?.inheritedCatalogs?.get(catalog.Id).parentName || '';
      return `${inheritedFromText}\n${parentName}`;
    }
    return '';
  }

  public isEnvironmentCategory(category: IKBCatalogCategory): boolean {
    return category.DisplayName?.trim().toLowerCase() === CategoryNames.ENVIRONMENT;
  }
  
  private clearKnowledgeSelection(): void {
    if (this.knowledgeCategories) {
      this.knowledgeCategories.forEach(category => {
        category.Selected = false;
        Object.values(category.KBCatalogs).forEach(catalog => {
          catalog.CanReset = false;
          catalog.Selected = false;
        });
      });
    }
  }

  private syncKnowledgeSelection(): void {
    if (!this.knowledgeCategories || !this.aIBpmKnowledgeBase) return;

    // Map for quick access by ref
    const categoryStates = new Map(
      this.aIBpmKnowledgeBase.kbCategories.map(item => [item.idParent, item.state])
    );
    const catalogStates = new Map(
      this.aIBpmKnowledgeBase.kbCatalogs.map(item => [item.idParent, item.state])
    );

    this.knowledgeCategories.forEach(category => {
      // Check if category name is exactly "Environment"
      const isEnvironmentCategory = this.isEnvironmentCategory(category);
      
      if (isEnvironmentCategory) {
        // Environment category is always selected
        // category.Selected = true;
        
        // Check individual catalogs
        category.KBCatalogs.forEach(catalog => {
          if (catalogStates.has(catalog.Id)) {
            catalog.CanReset = !this.inheritanceInfo?.inheritedCatalogs?.has(catalog.Id);
            catalog.Selected = catalogStates.get(catalog.Id) === ItemStateEnum.SET;
          } else {
            catalog.Selected = false;
          }
        });
      } else {
        // Original logic for non-Environment categories
        // First check if the category has a state
        if (categoryStates.has(category.Id)) {
          const categoryState = categoryStates.get(category.Id) === ItemStateEnum.SET;

          if (categoryState) {
            // If the category is selected, ALL catalogs must be selected
            category.Selected = true;
            category.KBCatalogs.forEach(catalog => {
              catalog.Selected = true;
            });
          } else {
            // If the category is NOT selected, check individual catalogs
            category.KBCatalogs.forEach(catalog => {
              if (catalogStates.has(catalog.Id)) {
                catalog.Selected = catalogStates.get(catalog.Id) === ItemStateEnum.SET;
              } else {
                catalog.Selected = false;
              }
            });
          }
        } else {
          // If there's no category state, check individual catalogs
          category.KBCatalogs.forEach(catalog => {
            if (catalogStates.has(catalog.Id)) {
              catalog.Selected = catalogStates.get(catalog.Id) === ItemStateEnum.SET;
            } else {
              catalog.Selected = false;
            }
          });
        }
      }
    });
  }

  private initializeCollapseState(): void {
    this.isCollapsed = [];
    this.knowledgeCategories.forEach((category, index) => {
      // If it has no catalogs or is empty, initialize as collapsed (true)
      this.isCollapsed[index] = !category.KBCatalogs || category.KBCatalogs.length === 0;
    });
  }
}
